import React, { type FC } from 'react';
import { Form, Popconfirm } from 'antd';
import { Button, Space } from '@g2u/ui-kit';
import { format, isValid, parseISO } from 'date-fns';

import { Condition, Service, PaymentMethod, type WithOptionalDataTest } from 'types';
import { PaymentSteps } from '../../hooks';

export namespace Actions {
  export type Props = {
    job: Service | null;
    step: PaymentSteps;
    onSendEmail: () => void;
    onGenerateInvoice: () => void;
    onRequestPayment: () => void;
    onProcessPayment: () => void;
    hasCard?: boolean;
    paymentMethod?: PaymentMethod;
    isLocked: boolean;
    isLoading: boolean;
  } & WithOptionalDataTest;

  export const $: FC<Props> = (props) => {
    const {
      job,
      step,
      onSendEmail,
      onGenerateInvoice,
      onRequestPayment,
      onProcessPayment,
      isLocked,
      hasCard,
      paymentMethod,
      isLoading,
    } = props;
    const form = Form.useFormInstance();
    Form.useWatch([], form);
    const selectedPaymentMethod = Form.useWatch('paymentMethod', form);
    const isDetailsConfirmed = Form.useWatch('isDetailsConfirmed', form);
    const isCardOnFileSelected = Form.useWatch('useCardOnFile', form) === Condition.Yes;
    const hasZeroServiceTotal = Number(job?.service_total) === 0;
    const isMethodSelected = isCardOnFileSelected || (!isCardOnFileSelected && selectedPaymentMethod);
    const canGenerateInvoice = isMethodSelected && isDetailsConfirmed;
    const appointmentTime =
      job?.products[0]?.service_technicians[0]?.appointment ||
      job?.attributes?.find((attribute) => attribute.key === 'requested_appointment')?.value;

    return (
      <Space.$ direction={Space.Direction.Horizontal} size={Space.Size.Small} justify={Space.Justify.End}>
        {step !== PaymentSteps.GenerateInvoice && (
          <Button.$ dataTestId="email-invoice" onClick={onSendEmail} disabled={isLocked} loading={isLoading}>
            {step !== PaymentSteps.Paid ? 'Email Invoice' : 'Email Invoice & Receipt'}
          </Button.$>
        )}
        {step === PaymentSteps.GenerateInvoice && (
          <Popconfirm
            title="Closing Job Confirmation"
            onConfirm={onGenerateInvoice}
            okText="Yes"
            description={
              <div>
                Please confirm you are closing job #{job?.ID}
                <br />
                Appointment:{' '}
                {appointmentTime && isValid(appointmentTime)
                  ? format(parseISO(appointmentTime), 'h:mmaaa dd-MM-yyyy')
                  : 'N/A'}
                <br />
                Customer Name: {job?.customer?.name?.full_name}
                <br />
                Invoice Total: ${job?.service_total}
              </div>
            }
          >
            <div>
              <Button.$
                type={Button.Type.Primary}
                dataTestId="generate-invoice-btn"
                onClick={() => null}
                disabled={isLocked || !canGenerateInvoice}
                loading={isLoading}
              >
                Generate Invoice
              </Button.$>
            </div>
          </Popconfirm>
        )}
        {step === PaymentSteps.ProcessPayment && (
          <>
            {paymentMethod === PaymentMethod.CreditCard ? (
              hasCard && isCardOnFileSelected ? (
                <Popconfirm
                  title="Process Payment"
                  onConfirm={onProcessPayment}
                  okText="Yes"
                  description={<div>Do you wish to process payment by credit card on file?</div>}
                >
                  <div>
                    <Button.$
                      type={Button.Type.Primary}
                      dataTestId="process-payment-btn"
                      onClick={() => null}
                      disabled={isLocked || hasZeroServiceTotal}
                      loading={isLoading}
                    >
                      Process Payment
                    </Button.$>
                  </div>
                </Popconfirm>
              ) : (
                <Button.$
                  type={Button.Type.Primary}
                  dataTestId="request-payment-btn"
                  onClick={onRequestPayment}
                  disabled={isLocked || hasZeroServiceTotal}
                  loading={isLoading}
                >
                  Request Payment
                </Button.$>
              )
            ) : (
              <Popconfirm
                title="Save Receipt"
                onConfirm={onProcessPayment}
                okText="Yes"
                description={<div>Please confirm the customer has paid to us by cash or direct bank transfer.</div>}
              >
                <div>
                  <Button.$
                    type={Button.Type.Primary}
                    dataTestId="save-receipt-btn"
                    onClick={() => null}
                    disabled={isLocked || hasZeroServiceTotal}
                    loading={isLoading}
                  >
                    Save Receipt
                  </Button.$>
                </div>
              </Popconfirm>
            )}
          </>
        )}
      </Space.$>
    );
  };
}
